/**
 * Pipeline and Forecast Dashboard — W66.E refactor.
 *
 * W66.E: imports bucket definitions from canonical client/src/lib/forecast-buckets.ts.
 * Bucket spec, colors, and order are no longer defined locally.
 *
 * API: /api/dashboards/pipeline-forecast
 *   Returns: { buckets: Record<ForecastBucket, { count, totalValue, color }>, totalPipeline }
 *
 * No Recharts. No exports. Tenant 1 only.
 */

import { useEffect, useState } from "react";
import { getSession } from "@/lib/session";
import { ForecastBuckets } from "@/components/charts/svg/ForecastBuckets";
import { BUCKET_ORDER, SCORE_BAND_COLORS, type ForecastBucket } from "@/lib/forecast-buckets";

interface BucketData { count: number; totalValue: number; color: string; }
interface PFData {
  buckets: Record<string, BucketData>;
  totalPipeline: number;
}

function fmt(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

export default function PipelineForecast() {
  const session = getSession();
  const [data, setData] = useState<PFData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = session?.token;
    if (!token) { setError("Not authenticated"); setLoading(false); return; }
    fetch("/api/dashboards/pipeline-forecast", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => {
        console.error("[pipeline-forecast]", e);
        setError("Could not load pipeline data");
        setLoading(false);
      });
  }, [session?.token]);

  const totalOpen = data
    ? BUCKET_ORDER.reduce((s, k) => s + (data.buckets[k]?.totalValue ?? 0), 0)
    : 0;

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-[15px] font-semibold text-foreground">Pipeline and Forecast</h2>
      {error && <p className="text-[12px] text-[#DC2626]">{error}</p>}

      {data && (
        <div className="flex flex-wrap gap-6 text-[13px]">
          <div>
            <span className="text-muted-foreground">Total open pipeline</span>
            <span className="ml-2 font-semibold tabular-nums text-foreground">
              {fmt(totalOpen)}
            </span>
          </div>
        </div>
      )}

      {/* Bucket legend strip */}
      {data && (
        <div className="flex flex-wrap gap-3 text-[12px]">
          {BUCKET_ORDER.map((b) => {
            const bd = data.buckets[b];
            return (
              <div key={b} className="flex items-center gap-1.5">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                  style={{ background: SCORE_BAND_COLORS[b as ForecastBucket] }}
                />
                <span className="text-muted-foreground">{b}</span>
                <span className="font-semibold tabular-nums text-foreground">
                  {bd?.count ?? 0}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4">
        <h3 className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wide mb-4">
          Forecast Bucket Composition
        </h3>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-8 w-full bg-[hsl(var(--surface-2))] rounded animate-pulse" />
            ))}
          </div>
        ) : data ? (
          <ForecastBuckets
            buckets={data.buckets as any}
            totalPipeline={data.totalPipeline}
          />
        ) : null}
      </div>
    </div>
  );
}
